import { useEffect, useRef, useState } from "react";
import "../css/TestimonialPage.css";

import video1 from "../testimonialVideos/TestimonialsVideo1.mp4";
import video2 from "../testimonialVideos/TestimonialsVideo2.mp4";
import video3 from "../testimonialVideos/feedvideo3.mp4";

const videos = [
  {
    id: 1,
    src: video1,
    number: "01",
    tag: "Community Story",
    title: "Learning experiences that make an impact",
    accent: "yellow",
  },
  {
    id: 2,
    src: video2,
    number: "02",
    tag: "MindSpark Experience",
    title: "Technology learning beyond the classroom",
    accent: "blue",
  },
  {
    id: 3,
    src: video3,
    number: "03",
    tag: "Learning Journey",
    title: "Curiosity, creativity and confidence",
    accent: "purple",
  },
];

export default function TestimonialPage() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  const containerRef = useRef(null);
  const videoRef = useRef(null);

  const currentVideo = videos[currentIndex];

  /* ======================================================
     DETECT WHEN SECTION IS VISIBLE
  ====================================================== */

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      {
        threshold: 0.35,
      }
    );

    const section = containerRef.current;

    if (section) {
      observer.observe(section);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  /* ======================================================
     PLAY / PAUSE CURRENT VIDEO
  ====================================================== */

  useEffect(() => {
    const video = videoRef.current;

    if (!video) return;

    if (isVisible) {
      const playPromise = video.play();

      if (playPromise !== undefined) {
        playPromise.catch(() => {
          video.muted = true;

          video.play().catch(() => {
            // Browser may block autoplay.
          });
        });
      }
    } else {
      video.pause();
    }
  }, [isVisible, currentIndex]);

  /* ======================================================
     AUTO CHANGE EVERY 20 SECONDS
  ====================================================== */

  useEffect(() => {
    if (!isVisible) return undefined;

    const timer = setTimeout(() => {
      setCurrentIndex(
        (prev) => (prev + 1) % videos.length
      );
    }, 20000);

    return () => {
      clearTimeout(timer);
    };
  }, [currentIndex, isVisible]);

  /* ======================================================
     CONTROLS
  ====================================================== */

  const previousVideo = () => {
    setCurrentIndex((prev) =>
      prev === 0
        ? videos.length - 1
        : prev - 1
    );
  };

  const nextVideo = () => {
    setCurrentIndex(
      (prev) => (prev + 1) % videos.length
    );
  };

  const selectVideo = (index) => {
    setCurrentIndex(index);
  };

  return (
    <section
      className="ms-voices-section"
      ref={containerRef}
    >
      {/* ==================================================
          BACKGROUND DECORATION
      ================================================== */}

      <div className="ms-voices-grid" />

      <div className="ms-voices-orb ms-orb-blue" />
      <div className="ms-voices-orb ms-orb-purple" />
      <div className="ms-voices-orb ms-orb-yellow" />

      <div className="ms-big-quote">
        “
      </div>

      <div className="ms-voices-dots-pattern" />

      <div className="container ms-voices-container">
        {/* ==================================================
            HEADING
        ================================================== */}

        <div className="ms-voices-heading-row">
          <div className="ms-voices-heading">
            <div className="ms-voices-kicker">
              <span />

              STORIES THAT INSPIRE
            </div>

            <h2>
              Hear learning through
              <span> real voices.</span>
            </h2>
          </div>

          <div className="ms-voices-heading-copy">
            <p>
              Hear stories from the MindSpark learning
              community and discover how technology-driven,
              hands-on learning creates meaningful experiences.
            </p>

            <div className="ms-voices-status">
              <span className="ms-status-dot" />

              <strong>
                {videos.length} stories
              </strong>

            </div>
          </div>
        </div>

        {/* ==================================================
            MAIN EXPERIENCE
        ================================================== */}

        <div className="ms-voices-experience">
          {/* ================================================
              VIDEO SIDE
          ================================================ */}

          <div className="ms-main-video-card">
            {/* TOP BAR */}
            <div className="ms-video-card-header">
              <div className="ms-video-story-number">
                <span>
                  STORY
                </span>

                <strong>
                  {currentVideo.number}
                </strong>
              </div>

              <div
                className={`ms-current-category ${currentVideo.accent}`}
              >
                <span />

                {currentVideo.tag}
              </div>
            </div>

            {/* VIDEO */}
            <div className="ms-voice-video-stage">
              <video
                key={currentVideo.id}
                ref={videoRef}
                src={currentVideo.src}
                className="ms-voice-video"
                controls
                muted
                playsInline
                preload="metadata"
                onEnded={nextVideo}
              />

              {/* PREVIOUS */}
              <button
                type="button"
                className="ms-voice-arrow ms-voice-prev"
                onClick={previousVideo}
                aria-label="Previous testimonial"
              >
                ‹
              </button>

              {/* NEXT */}
              <button
                type="button"
                className="ms-voice-arrow ms-voice-next"
                onClick={nextVideo}
                aria-label="Next testimonial"
              >
                ›
              </button>
            </div>

            {/* VIDEO DETAILS */}
            <div className="ms-video-details">
              <div>
                <span className="ms-now-showing">
                  NOW SHOWING
                </span>

                <h3>
                  {currentVideo.title}
                </h3>
              </div>

              <div className="ms-story-count">
                <strong>
                  {String(
                    currentIndex + 1
                  ).padStart(2, "0")}
                </strong>

                <span />

                {String(
                  videos.length
                ).padStart(2, "0")}
              </div>
            </div>

            {/* PROGRESS */}
            <div
              className={`ms-voice-progress ${currentVideo.accent}`}
              key={currentIndex}
            >
              <span />
            </div>
          </div>

          {/* ================================================
              STORY PLAYLIST
          ================================================ */}

          <aside className="ms-story-playlist">
            <div className="ms-playlist-header">
              <span className="ms-playlist-eyebrow">
                VOICES OF MINDSPARK
              </span>

              <h3>
                Choose a story
              </h3>

              <p>
                Explore experiences from our learning
                community.
              </p>
            </div>

            <div className="ms-story-list">
              {videos.map((video, index) => {
                const isActive =
                  index === currentIndex;

                return (
                  <button
                    type="button"
                    key={video.id}
                    className={`ms-story-item ${video.accent} ${
                      isActive ? "active" : ""
                    }`}
                    onClick={() =>
                      selectVideo(index)
                    }
                    aria-current={
                      isActive
                        ? "true"
                        : undefined
                    }
                  >
                    <div className="ms-story-item-number">
                      {video.number}
                    </div>

                    <div className="ms-story-item-content">
                      <span>
                        {video.tag}
                      </span>

                      <strong>
                        {video.title}
                      </strong>
                    </div>

                    <div className="ms-story-play-icon">
                      {isActive ? (
                        <span className="ms-playing-bars">
                          <i />
                          <i />
                          <i />
                        </span>
                      ) : (
                        "▶"
                      )}
                    </div>

                    {isActive && (
                      <div className="ms-active-story-line" />
                    )}
                  </button>
                );
              })}
            </div>

            {/* BOTTOM BRAND MESSAGE */}
            <div className="ms-playlist-brand">
              <div className="ms-brand-mark">
                ✦
              </div>

              <div>
                <strong>
                  Learning Through Technology
                </strong>

                <span>
                  MindSpark EduTech
                </span>
              </div>
            </div>
          </aside>
        </div>

        {/* ==================================================
            MOBILE / BOTTOM NAVIGATION
        ================================================== */}

        <div className="ms-voices-bottom-nav">
          <div className="ms-voices-dots">
            {videos.map((video, index) => (
              <button
                key={video.id}
                type="button"
                className={`ms-voice-dot ${
                  index === currentIndex
                    ? `active ${video.accent}`
                    : ""
                }`}
                onClick={() =>
                  selectVideo(index)
                }
                aria-label={`Play story ${index + 1}`}
              />
            ))}
          </div>

        </div>
      </div>
    </section>
  );
}
